import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/api/client';
import { buttonClassName } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField, TextField } from '@/components/ui/Field';
import { PageHeader } from '@/components/ui/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { EmptyState, LoadingLine } from '@/components/ui/States';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import { useCan, useCurrentUser } from '@/features/admin-content/use-current-user';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/bank/')({
  component: BankListPage,
});

function BankListPage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const allowed = useCan('content.admin');
  const [skill, setSkill] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['admin', 'bank', skill, status, q],
    enabled: allowed,
    queryFn: () =>
      api.get('/api/v1/admin/bank/stimuli', {
        query: {
          ...(skill ? { skill } : {}),
          ...(status ? { status } : {}),
          ...(q ? { q } : {}),
          pageSize: '50',
        },
      }),
  });

  if (sessionPending || meQuery.isPending) {
    return <LoadingLine>Loading</LoadingLine>;
  }
  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }
  if (!allowed) {
    return (
      <StaffOnly title="You cannot open the bank" description="Ask an admin for staff access." />
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Workspace"
        title="Content bank"
        description="Create and edit practice material. A change makes a new version, so old student work stays as they saw it."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/bank/blueprints" className={buttonClassName({ variant: 'secondary' })}>
              Forms
            </Link>
            <Link to="/admin/bank/import" className={buttonClassName({ variant: 'secondary' })}>
              Import file
            </Link>
            <Link to="/admin/bank/new" className={buttonClassName()}>
              Create stimulus
            </Link>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <SelectField
          label="Skill"
          value={skill}
          onChange={(event) => {
            setSkill(event.target.value);
          }}
        >
          <option value="">All skills</option>
          <option value="listening">Listening</option>
          <option value="reading">Reading</option>
          <option value="writing">Writing</option>
          <option value="speaking">Speaking</option>
        </SelectField>
        <SelectField
          label="Status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
          }}
        >
          <option value="">Any status</option>
          <option value="draft">Draft</option>
          <option value="inReview">In review</option>
          <option value="pilot">Pilot</option>
          <option value="published">Published</option>
          <option value="retired">Retired</option>
        </SelectField>
        <TextField
          label="Title contains"
          value={q}
          onChange={(event) => {
            setQ(event.target.value);
          }}
        />
      </div>

      {list.isError ? (
        <Alert tone="danger" title="We could not load the bank">
          Refresh the page, then try again.
        </Alert>
      ) : null}
      {list.isPending ? <LoadingLine>Loading the bank</LoadingLine> : null}
      {list.data && list.data.items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Nothing matches"
          description="Create a stimulus, or clear the filters."
        />
      ) : null}
      {list.data && list.data.items.length > 0 ? (
        <Card padding="none">
          <Table>
            <TableHead>
              <TableHeader>Title</TableHeader>
              <TableHeader>Skills</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Version</TableHeader>
              <TableHeader>Items</TableHeader>
            </TableHead>
            <TableBody>
              {list.data.items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Link
                      to="/admin/bank/$stimulusId"
                      params={{ stimulusId: row.id }}
                      className="font-bold text-primary"
                    >
                      {row.title}
                    </Link>
                  </TableCell>
                  <TableCell>{row.skills.join(', ') || '—'}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.currentVersionNumber ?? 'draft'}</TableCell>
                  <TableCell>{row.itemCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
